import _ from 'lodash';
import { CLIENT_STATE_KEYS, CLIENT_STATE_VERSIONS, } from '../../client-state-type';
import { BackgroundFormatTypes, ChartType, ChartVizPropVersion, ConditionalFormattingComparisonTypes, } from '../../types/prism';
import { ClientStateV4dot1Schema, DeprecatedClientStateV4Dot1Schema, } from '../types/v4dot1';
import { ClientStateV4dot2Class } from '../types/v4dot2';
import { getUpgradedAxisPropertiesForV4dot1, getUpgradedChartPropertiesForV4, getUpgradedMultiSeriesColors, getUpgradedSeriesColors, parseConditionalFormattingFromClientStateToVizProp, } from './v4-to-v4dot1';
const updateClientStateForHeatmapAndTreemap = (vizModel, updatedClientState) => {
    const isHeatmap = vizModel.chartType === ChartType.Heatmap;
    const isTreemap = vizModel.chartType === ChartType.Treemap;
    if (isHeatmap || isTreemap) {
        const overrides = {
            seriesColors: [],
            systemSeriesColors: [],
        };
        _.assign(updatedClientState, overrides);
    }
};
export const DEFAULT_CONDITIONAL_METRIC_V4DOT2 = {
    __typename: 'ConditionalMetric',
    operator: null,
    fontProperties: null,
    gradientBackgroundAttrs: null,
    rangeValues: null,
    value: null,
    backgroundFormatType: BackgroundFormatTypes.Solid,
    plotAsBand: null,
    solidBackgroundAttrs: {
        __typename: 'SolidBackgroundAttrs',
        color: null,
    },
    comparisonType: ConditionalFormattingComparisonTypes.ValueBased,
    lhsColumnId: null,
    rhsColumnId: null,
    comparisonParameterId: null,
    isHighlightRow: false,
};
const getUpgradedColumnPropertiesForV4dot2 = (clientState) => {
    const columnProperties = _.get(clientState, CLIENT_STATE_KEYS.columnProperties, []);
    return columnProperties.map(obj => ({
        __typename: 'ChartColumnPropertyMap',
        columnId: obj.columnId,
        columnProperty: {
            __typename: 'ChartVizColumnProperties',
            dataLabels: _.get(obj.columnProperty, [CLIENT_STATE_KEYS.dataLabels], null),
            conditionalFormatting: parseConditionalFormattingFromClientStateToVizProp(obj.columnProperty, DEFAULT_CONDITIONAL_METRIC_V4DOT2),
            handleMissingValues: null,
            excludeNullValues: null,
            customProps: null,
        },
    }));
};
const getUpgradedAxisPropertiesForV4dot2 = (clientState) => {
    const axisProperties = getUpgradedAxisPropertiesForV4dot1(clientState);
    const updatedAxisProperties = [];
    axisProperties.forEach((axisObj) => {
        var _a;
        updatedAxisProperties.push({
            __typename: 'AxisObj',
            id: axisObj.id,
            properties: {
                ...axisObj.properties,
                dynamicTitle: (_a = axisObj.properties.dynamicTitle) !== null && _a !== void 0 ? _a : null,
            },
        });
    });
    return updatedAxisProperties;
};
const upgradeV4Dot1ToV4dot2 = (clientState, vizModel) => {
    var _a, _b, _c, _d, _e;
    const upgradedColumnPropertiesForV4dot1 = getUpgradedChartPropertiesForV4(clientState);
    const updatedClientState = {
        __typename: 'ChartVizProps',
        axisProperties: getUpgradedAxisPropertiesForV4dot2(clientState),
        chartProperties: {
            ...upgradedColumnPropertiesForV4dot1,
            mapviewport: upgradedColumnPropertiesForV4dot1.mapviewport
                ? {
                    ...upgradedColumnPropertiesForV4dot1.mapviewport,
                    extent: (_a = upgradedColumnPropertiesForV4dot1.mapviewport
                        .extent) !== null && _a !== void 0 ? _a : null,
                }
                : null,
            chartSpecific: upgradedColumnPropertiesForV4dot1.chartSpecific
                ? {
                    ...upgradedColumnPropertiesForV4dot1.chartSpecific,
                    dataFieldArea: (_b = upgradedColumnPropertiesForV4dot1.chartSpecific
                        .dataFieldArea) !== null && _b !== void 0 ? _b : 'column',
                    showPivotSummaryPrior: null,
                    pivotSummariesState: null,
                }
                : null,
            showLegend: (_c = upgradedColumnPropertiesForV4dot1.showLegend) !== null && _c !== void 0 ? _c : null,
            legendPosition: (_d = upgradedColumnPropertiesForV4dot1.legendPosition) !== null && _d !== void 0 ? _d : null,
        },
        columnProperties: getUpgradedColumnPropertiesForV4dot2(clientState),
        customColorSelectorArray: _.get(clientState, CLIENT_STATE_KEYS.customColorSelectorArray, []),
        seriesColors: getUpgradedSeriesColors(clientState, CLIENT_STATE_KEYS.SERIES_COLORS),
        systemSeriesColors: getUpgradedSeriesColors(clientState, CLIENT_STATE_KEYS.SYSTEM_SERIES_COLORS),
        multiColorSeriesColors: getUpgradedMultiSeriesColors(clientState, CLIENT_STATE_KEYS.MULTI_COLOR_SERIES_COLORS),
        systemMultiColorSeriesColors: getUpgradedMultiSeriesColors(clientState, CLIENT_STATE_KEYS.SYSTEM_MULTI_COLOR_SERIES_COLORS),
        tooltipConfig: (_e = clientState.tooltipConfig) !== null && _e !== void 0 ? _e : null,
        version: ChartVizPropVersion.V4Dot2,
    };
    updateClientStateForHeatmapAndTreemap(vizModel, updatedClientState);
    const clientStateV4dot2 = new ClientStateV4dot2Class();
    _.assign(clientStateV4dot2, updatedClientState);
    return clientStateV4dot2;
};
const upgraderFn = (clientState, vizModel) => {
    const validateClientStateV4dot1Schema = ClientStateV4dot1Schema.validate(clientState);
    const validateDeprecatedClientStateV4Dot1Schema = DeprecatedClientStateV4Dot1Schema.validate(clientState);
    if (validateClientStateV4dot1Schema.error &&
        validateDeprecatedClientStateV4Dot1Schema.error) {
        throw new Error(`[ClientStateV4dot1Schema] : ${validateClientStateV4dot1Schema.error.message}, [DeprecatedClientStateV4Dot1Schema] : ${validateDeprecatedClientStateV4Dot1Schema.error.message}`);
    }
    const clientStateVersion = _.get(clientState, CLIENT_STATE_KEYS.version);
    if (clientStateVersion !== CLIENT_STATE_VERSIONS.V4dot1) {
        throw new Error(`incompatible client state received in chart upgrade path for v4.1 to v4.2 with version no:${clientStateVersion}`);
    }
    return upgradeV4Dot1ToV4dot2(clientState, vizModel);
};
const testHooks = {
    upgradeV4Dot1ToV4dot2,
};
export { upgraderFn, testHooks };
//# sourceMappingURL=v4dot1-to-v4dot2.js.map